
import { useEffect, useRef, useState } from 'react';

export default function MarketingPromise() {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const promises = [
    {
      icon: 'ri-search-line',
      title: 'Bedarfs- & Nichenanalyse',
      description: 'Uns ist eine transparente Zusammenarbeit wichtig: In einer kostenfreien Beratung analysieren wir gemeinsam, was Ihnen wichtig ist, welche Ziele Sie erreichen wollen und wie wir von BlecInk Sie dabei unterstützen können. Erst dann erarbeiten wir eine maßgeschneiderte Strategie für Ihr Unternehmen.'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Top-Platzierung in den Google Suchergebnissen',
      description: 'In nur 3–4 Monaten positionieren wir Ihr Unternehmen in Ihrer Region unter den führenden Suchergebnissen. Und falls wir dieses Ziel nicht erreichen, bekommen Sie Ihr Geld vollständig zurück.'
    },
    {
      icon: 'ri-share-line',
      title: 'Social Media Management',
      description: 'Mit unserem Social Media Management stärken wir Ihre Online-Präsenz, erstellen ansprechende Inhalte und steigern nachhaltig Ihre Reichweite.'
    }
  ];

  return (
    <section id="marketing-promise" className="py-20 bg-gradient-to-b from-gray-900 to-gray-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" ref={sectionRef}>
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center">
            Unser <span className="text-teal-400">Marketingversprechen</span>
          </h2>
        </div>

        <div className="flex justify-center mb-8">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-3 bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg cursor-pointer whitespace-nowrap"
          >
            <span>{isExpanded ? 'Weniger anzeigen' : 'Mehr erfahren'}</span>
            <i className={`ri-arrow-down-s-line text-2xl transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}></i>
          </button>
        </div>

        <div 
          className="overflow-hidden transition-all duration-500 ease-in-out"
          style={{
            maxHeight: isExpanded ? '2000px' : '0',
            opacity: isExpanded ? 1 : 0
          }}
        >
          <div className="mb-8">
            <p className="text-lg text-gray-300 max-w-3xl mx-auto text-center">
              Wir stehen für Qualität, Transparenz und messbare Ergebnisse. Entdecken Sie, wie wir Ihr Unternehmen zum Erfolg führen.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {promises.map((promise, index) => (
              <div 
                key={index}
                className={`group relative bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm p-8 rounded-2xl border border-gray-700/50 hover:border-teal-500/50 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-teal-500/10 ${
                  isVisible 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-12'
                }`}
                style={{
                  transitionDelay: `${index * 150}ms`,
                  transitionProperty: 'opacity, transform, border-color, box-shadow'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-teal-600/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl mb-6 group-hover:from-teal-500 group-hover:to-teal-600 transition-all duration-300">
                    <i className={`${promise.icon} text-3xl text-white`}></i>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-teal-100 transition-colors duration-300">
                    {promise.title}
                  </h3>
                  
                  <p className="text-gray-300 leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
                    {promise.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Neue schmale Box */}
          <div className="mt-8 bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm p-6 rounded-xl border border-gray-700/50">
            <h3 className="text-xl font-bold text-white mb-3 text-center">
              Regionale Bekanntheit
            </h3>
            <p className="text-gray-300 leading-relaxed text-center">
              Wir konzentrieren uns darauf, Ihre Zielgruppe effektiv zu erreichen – für mehr Bekanntheit und höhere Profitabilität, nicht nur für schöne Statistiken.
            </p>
          </div>

          <div className="mt-12 bg-gray-800/90 backdrop-blur-sm p-8 rounded-xl border border-gray-700 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Bereit für Ihren Marketing-Erfolg?
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Vereinbaren Sie noch heute eine kostenlose Beratung und erfahren Sie, wie wir Ihr Unternehmen zum Erfolg führen können.
            </p>
            <div className="flex justify-center">
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white px-6 sm:px-10 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-teal-500/25 cursor-pointer whitespace-nowrap"
              >
                Kostenlose Beratung vereinbaren
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
